<template>
  <v-container v-resize="getWindowWidth">
    <v-row justify="end">
      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <v-card>
          <v-card-text>
            <div
              class="subtitle-2 grey--text text--darken-1 font-weight-medium"
            >
              {{ $t('users.title') }}
            </div>
            <div
              class="display-1 text-right yellow--text text--darken-4 font-weight-black"
            >
              {{ totalUsers }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-card class="rounded-card ma-3">
        <v-card-title
          class="subtitle-2 grey--text text--darken-1 font-weight-medium "
        >
          {{ $t('nps.admin.title') }}
        </v-card-title>
        <v-divider />
        <div style="display: flex; flex-wrap:wrap;padding:0.5rem 0 0.5rem 1rem">
          <div style="width:100%; font-size:1rem;">
            {{ $t('nps.admin.rating') }} :
            <span class="custom-font-size primary--text font-weight-black">{{
              this.npsValue
            }}</span>
          </div>

          <div style="width:100%; font-size:1rem;">
            {{ $t('nps.admin.reviews') }} :
            <span class="custom-font-size primary--text font-weight-black ">
              {{ this.reviewCount }}</span
            >
          </div>
        </div>
      </v-card>
    </v-row>

    <v-layout wrap>
      <v-flex xs12 pt-4>
        <v-card>
          <v-card-title>
            {{ $t('users.title') }}
            <v-spacer></v-spacer>
            <v-btn
              ref="button-open-export-dialog"
              @click="toggleDialogExport(true)"
              small
              color="primary"
              class="mr-2"
            >
              <v-icon left>cloud_download</v-icon>
              {{ $t('general.export') }}
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row justify="end">
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="search"
                  append-icon="search"
                  :label="$t('general.search')"
                  single-line
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-data-table
            :headers="headers"
            :items="users"
            :search.sync="search"
            :page.sync="pagination.page"
            :items-per-page.sync="pagination.itemsPerPage"
            :sort-by.sync="pagination.sortBy"
            :sort-desc.sync="pagination.sortDesc"
            :server-items-length="pagination.total"
            class="elevation-1"
            :items-per-page-options="[25, 50, 100, -1]"
            :footer-props="{
              'items-per-page-options': [5, 20, 50, 100]
            }"
            @update:page="getUsers"
            @update:items-per-page="getUsers"
            @update:sort-by="getUsers"
            @update:sort-desc="getUsers"
          >
            <template v-slot:item="{ item }">
              <tr @click="openUserDrawer(item.id)">
                <td>
                  <v-list-item class="px-0">
                    <v-list-item-avatar
                      class="mr-4"
                      :color="generateRandomColor()"
                    >
                      <span class="white--text">
                        {{ `${item.person && item.person.first_name[0]}` }}
                        {{
                          `${
                            item.person && item.person.last_name
                              ? item.person.last_name[0]
                              : ''
                          }`
                        }}
                      </span>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        <span>
                          {{
                            `${item.person && item.person.first_name} ${
                              item.person && item.person.last_name
                                ? item.person.last_name
                                : ''
                            }`
                          }}
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </td>
                <td>
                  <v-tooltip bottom v-if="item.email.length > 20" color="black">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        {{
                          item.email.length > 20
                            ? `${item.email.slice(0, 18)}...`
                            : item.email
                        }}
                      </span>
                    </template>
                    {{ item.email }}
                  </v-tooltip>
                  <span v-else>
                    {{ item.email }}
                  </span>
                </td>
                <td>
                  {{ item.person && item.person.phone }}
                </td>
                <td>
                  {{ item.__meta__.organizations_count }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
    <v-navigation-drawer
      :value="userDrawerState"
      :permanent="userDrawerState"
      temporary
      fixed
      right
      width="calc(100vw - 84px)"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
      class="bg-gray"
    >
      <UserView v-show="userDrawerState" />
    </v-navigation-drawer>
    <v-navigation-drawer
      :value="AssignOrgDrawerState"
      :permanent="AssignOrgDrawerState"
      temporary
      fixed
      right
      width="600px"
      max-width="100vw"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
      class="bg-gray"
    >
      <AssignOrganizationView v-show="AssignOrgDrawerState" />
    </v-navigation-drawer>
    <v-dialog v-model="isLoading" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          {{ $t('general.loading') }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <DialogExport
      :module-options="['AllUsersOrganization', 'FilteredUsersOrganization']"
    />
    <DialogMessage />
  </v-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import UserView from '@/components/User/UserView'
import AssignOrganizationView from '@/components/User/AssignOrganizationDrawer'
import DialogExport from '@/components/Downloads/DialogExport'
import DialogMessage from '@/components/Downloads/DialogMessage'

export default {
  name: 'Users',
  components: { UserView, AssignOrganizationView, DialogExport, DialogMessage },
  watch: {
    search(newVal, oldVal) {
      this.getUsers()
    }
  },
  data() {
    return {
      reviewCount: null,
      npsValue: null,
      isLoading: false,
      search: '',
      pagination: {
        page: 1,
        itemsPerPage: 5,
        sortBy: '',
        sortDesc: false,
        total: 0
      },
      headers: [
        {
          text: this.$t('users.table.name'),
          value: 'person.first_name',
          class: 'text-no-wrap blue--text text--darken-4',
          width: '',
          sortable: false
        },
        {
          text: this.$t('users.table.email'),
          value: 'email',
          class: 'text-no-wrap blue--text text--darken-4',
          width: '',
          sortable: true
        },
        {
          text: this.$t('users.table.phone'),
          value: 'person.phone',
          class: 'text-no-wrap blue--text text--darken-4',
          width: '',
          sortable: false
        },
        {
          text: this.$t('users.table.organizations'),
          value: 'organizations.name',
          class: 'text-no-wrap blue--text text--darken-4',
          width: '',
          sortable: false
        }
      ]
    }
  },
  async created() {
    this.isLoading = true
    await this.getUsers()
    this.isLoading = false
    this.getUsersModules()
    const { reviewCount, npsTotal } = (
      await axios.get('/nps/aggregatedData/')
    ).data
    this.reviewCount = reviewCount
    this.npsValue = npsTotal
  },
  methods: {
    ...mapActions({
      getAll: 'user/getAll',
      getUser: 'user/getUser',
      getUsersModules: 'userModules/getUsersModules'
    }),
    ...mapMutations({
      changeUser: 'user/changeUser',
      userDrawer: 'user/userDrawer',
      toggleDialogExport: 'downloads/toggleDialogExport',
      setUserId: 'user/setUserId'
    }),
    getWindowWidth() {
      if (process.client) {
        this.width =
          window.innerWidth > 800 ? window.innerWidth * 0.94 : window.innerWidth
      }
    },
    generateRandomColor() {
      var randomColor = '#000000'.replace(/0/g, function() {
        return (~~(Math.random() * 16)).toString(16)
      })
      return randomColor
    },
    openUserDrawer(id) {
      this.setUserId(id)
      this.userDrawer(true)
    },
    /**
     * getUsers Paginate
     */
    async getUsers() {
      const { search } = this
      const { page, sortBy, sortDesc, itemsPerPage } = this.pagination
      try {
        const { total } = await this.getAll({
          page,
          sortBy,
          sortDesc,
          itemsPerPage,
          search
        })
        this.pagination.total = Number(total)
      } catch (err) {
        console.log(err)
      }
    }
  },
  computed: {
    ...mapState({
      users: ({ user: { users } }) => users,
      totalUsers: ({ user: { totalUsers } }) => totalUsers,
      userId: ({ user: { userId } }) => userId,
      user: ({ user: { user } }) => user,
      userDrawerState: ({ user: { userDrawer } }) => userDrawer,
      AssignOrgDrawerState: ({ user: { AssignOrgDrawer } }) => AssignOrgDrawer
    })
  }
}
</script>

<style>
.custom-font-size {
  font-size: 1.2rem;
}
</style>
